import useProductStore from '@/store/product'
import useCartStore from '@/store/cart'
import useSettingStore from '@/store/setting'
import {
  QUERY_STRING_NO_CACHE,
  PRODUCT_COMBO_HANDLE_ATTRIBUTE_KEY,
} from '@/utils'
import { useRoute } from 'vue-router'

export const useProductCombo = () => {
  const productStore = useProductStore()
  const cartStore = useCartStore()
  const settingStore = useSettingStore()
  const route = useRoute()

  const isForceLoadNewData = !!route.query[QUERY_STRING_NO_CACHE]

  async function getCombos() {
    const combos = []
    const combosActive = getCombosActive()
    if (!combosActive.length) return combos

    const combosTransfered = transferCombos(combosActive)

    for (let i = 0; i < combosTransfered.length; i++) {
      const product = await productStore.getProductByHandle({
        handle: combosTransfered[i].upSellProduct.handle,
        isForceLoadNewData,
      })
      if (product) {
        combos.push({
          comboName: combosTransfered[i].comboName,
          product: {
            ...combosTransfered[i].product,
            ...productStore.product,
          },
          upSellProduct: {
            ...combosTransfered[i].upSellProduct,
            ...product,
          },
          discountValue: combosTransfered[i].value,
          discountType: combosTransfered[i].discountType,
        })
      }
    }

    return combos
  }

  function getCombosActive() {
    const combosSettings =
      settingStore.baseProductPage?.productComboSettings || []

    if (!combosSettings.length) return []

    const product = productStore.product
    if (!product) return []

    const combosActive = combosSettings.filter(
      (item: any) =>
        (item.product1.handle == product?.handle ||
          item.product2.handle == product?.handle) &&
        item.status
    )

    return combosActive
  }

  function transferCombos(combosActive: any) {
    if (!combosActive?.length) return []

    const product = productStore.product
    if (!product) return []

    const combosTransfered = combosActive.map((item: any) => {
      return {
        comboName: item.comboName,
        product:
          item.product1.handle == product?.handle
            ? item.product1
            : item.product2,
        upSellProduct:
          item.product2.handle != product?.handle
            ? item.product2
            : item.product1,
        value: item.value,
        discountType: item.discountType,
      }
    })

    return combosTransfered
  }

  function getComboCartLineItems() {
    const comboLineItems = []
    cartStore.localCartLineItems.forEach((lineItem) => {
      const comboAttribute = lineItem.attributes.find(
        (item) => item.key == PRODUCT_COMBO_HANDLE_ATTRIBUTE_KEY && item?.value
      )
      if (comboAttribute) comboLineItems.push(lineItem)
    })
    return comboLineItems
  }

  function getProductHandleComboAttribute(lineItems: any) {
    const comboAttribute = lineItems.attributes.find(
      (item) => item.key == PRODUCT_COMBO_HANDLE_ATTRIBUTE_KEY && item?.value
    )
    if (comboAttribute) return comboAttribute.value
  }

  function getComboCartLineItemsNotCompleted() {
    const comboLineItemsNotCompleted = []
    const comboLineItemIdsCompleted = []

    const comboCartLineItems = getComboCartLineItems()

    if (!comboCartLineItems?.length) return comboLineItemsNotCompleted

    for (let i = 0; i < comboCartLineItems.length; i++) {
      const lineItem = comboCartLineItems[i]
      const productHandle = getProductHandleComboAttribute(lineItem)

      const isComboLineItemCompleted = comboCartLineItems.some((lineItem2) => {
        const productHandle2 = getProductHandleComboAttribute(lineItem2)

        if (
          lineItem.variant.product.handle == productHandle2 &&
          productHandle == lineItem2.variant.product.handle &&
          !comboLineItemIdsCompleted.includes(lineItem.id) &&
          !comboLineItemIdsCompleted.includes(lineItem2.id)
        ) {
          comboLineItemIdsCompleted.push(lineItem.id)
          comboLineItemIdsCompleted.push(lineItem2.id)
          return true
        }
        return false
      })

      if (
        !isComboLineItemCompleted &&
        !comboLineItemIdsCompleted.includes(lineItem.id)
      ) {
        comboLineItemsNotCompleted.push(lineItem)
      }
    }
    return comboLineItemsNotCompleted
  }

  function getComboCartLineItemNotCompleted() {
    const comboLineItemsNotCompleted = getComboCartLineItemsNotCompleted()

    if (!comboLineItemsNotCompleted.length) return

    const comboLineItemNotCompleted = comboLineItemsNotCompleted.find(
      (lineItem) => {
        const product = productStore.product
        const productComboHandle = getProductHandleComboAttribute(lineItem)
        const comboSelected = productStore.productComboSelected
        if (
          productComboHandle == product?.handle &&
          comboSelected?.upSellProduct.handle == lineItem.variant.product.handle
        ) {
          return true
        }
        return false
      }
    )
    return comboLineItemNotCompleted
  }

  function getLineItemIdNeedUpdateComboAttribute() {
    const comboSelected = productStore.productComboSelected

    let lineItemNeedUpdateAttribute
    let productComboHandleAttribute

    const comboLineItemsNotCompleted = getComboCartLineItemsNotCompleted()

    if (!comboLineItemsNotCompleted.length)
      return [lineItemNeedUpdateAttribute, productComboHandleAttribute]

    const productUpSellHanldeSelected = comboSelected.upSellProduct?.handle
    const product = productStore.product

    lineItemNeedUpdateAttribute = comboLineItemsNotCompleted.find(
      (item) => item.variant.product.handle == product.handle
    )

    if (lineItemNeedUpdateAttribute) {
      productComboHandleAttribute = productUpSellHanldeSelected
    }

    if (!lineItemNeedUpdateAttribute) {
      lineItemNeedUpdateAttribute = comboLineItemsNotCompleted.find(
        (item) =>
          item.variant.product.handle == productUpSellHanldeSelected &&
          product.handle == comboSelected.product.handle
      )

      if (lineItemNeedUpdateAttribute) {
        const attributeProductHandle = getProductHandleComboAttribute(
          lineItemNeedUpdateAttribute
        )
        if (attributeProductHandle == comboSelected.product.handle) {
          lineItemNeedUpdateAttribute = null
        } else {
          productComboHandleAttribute = comboSelected.product.handle
        }
      }
    }

    return [lineItemNeedUpdateAttribute, productComboHandleAttribute]
  }

  function getIndexOptionComboNeedComplete(combos: any[]) {
    const comboLineItemsNotCompleted = getComboCartLineItemsNotCompleted()
    if (!comboLineItemsNotCompleted.length) return 0
    let index = -1
    comboLineItemsNotCompleted.forEach((lineItem) => {
      const productHandle = getProductHandleComboAttribute(lineItem)

      if (index == -1) {
        index = combos.findIndex(
          (item) =>
            item.product.handle == productHandle &&
            item.upSellProduct.handle == lineItem.variant.product.handle
        )
      }
    })
    return index > -1 ? index : 0
  }

  return {
    getCombos,
    getCombosActive,
    getComboCartLineItemNotCompleted,
    getLineItemIdNeedUpdateComboAttribute,
    getIndexOptionComboNeedComplete,
  }
}
